import Lookup from '@/components/UI/Lookup';
import { _isEmpty, _isNotEmpty, _notNil } from '@/littledash';
import type { StudyInfoV1 } from '@/model/Study.model';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { FC, useState } from 'react';
import InVivoError from '@/model/InVivoError';
import { ExceptionHandler } from '@/utils/ExceptionHandler';

export interface StudyLookupProps {
  onChange: (study?: StudyInfoV1) => void;
  onBlur?: () => void;
  isDisabled?: boolean;
  placeholder?: string;
  isStudyDisabled?: (study: StudyInfoV1) => boolean;
}

export interface OptionType {
  label: string;
  value: StudyInfoV1 | undefined;
  disabled?: boolean;
}

export const StudyLookup: FC<StudyLookupProps> = ({
  onChange,
  onBlur = () => {},
  isDisabled = false,
  placeholder,
  isStudyDisabled,
}) => {
  // Internal option state for the Lookup component
  const [selectedOption, setSelectedOption] = useState<OptionType | null>(null);

  const { invoke: getStudies, loading: isLoading } = useApiHook({
    endpoint: 'GET /api/v1/studies',
    invokeOnInit: false,
  });

  const loadOptions = async ({ inputValue, page }: { inputValue: string; page: string }) => {
    try {
      const query: Record<string, string> = {
        perPage: '10',
        page,
      };

      const trimmedQuery = (inputValue ?? '').trim();
      if (_isNotEmpty(trimmedQuery)) {
        query.search_by = trimmedQuery;
      }

      const response = await getStudies({ query });

      if (response.type === 'success') {
        const studies = response.body.data ?? Array<StudyInfoV1>();
        const hasMore = _notNil(response.body.links?.next);

        const options: Array<OptionType> = studies.map((study) => {
          const disabled = isStudyDisabled ? isStudyDisabled(study) : false;

          const baseLabel = study.code ? `${study.code} · ${study.name}` : study.name;

          const enhancedLabel = disabled ? `${baseLabel} (⚠️ Folder not set)` : baseLabel;

          return {
            label: enhancedLabel,
            value: study,
            disabled,
          };
        });

        let nextPage = page;
        if (hasMore && _notNil(response.body.links?.next)) {
          const nextUrl = new URL(response.body.links.next);
          nextPage = nextUrl.searchParams.get('page') ?? '';
        }

        return {
          options,
          hasMore,
          additional: { page: nextPage },
        };
      }
    } catch (error) {
      ExceptionHandler.captureException(
        new InVivoError('Error loading studies for lookup', {
          cause: error,
          slug: 'study-lookup-load-options',
          level: 'warning',
        })
      );
    }

    return { options: Array<OptionType>(), hasMore: false, additional: { page } };
  };

  // Handle selection and reset internal state
  const handleSelect = (option: OptionType) => {
    // Don't allow selection of disabled options (extra safety, though react-select should handle this)
    if (option?.disabled) {
      return;
    }

    // Notify parent of the selected study
    onChange?.(option?.value);

    // Reset internal state to allow for a new selection
    setSelectedOption(null);
  };

  // Function to determine if an option should be disabled for react-select
  const isOptionDisabled = (option: unknown) => {
    // Type guard to check if option has the expected structure
    if (option && typeof option === 'object' && 'disabled' in option) {
      return Boolean((option as OptionType).disabled);
    }
    return false;
  };

  return (
    <Lookup
      name="study"
      defaultValue={null}
      value={selectedOption}
      loadOptions={loadOptions}
      defaultPagination={true}
      handleSelect={handleSelect}
      onBlur={onBlur}
      disabled={isDisabled}
      additional={{ page: '1', perPage: 10 }}
      isLoading={isLoading}
      placeholder={placeholder}
      isOptionDisabled={isOptionDisabled}
      noOptionsMessage={({ inputValue = '' }) =>
        isLoading ? 'Loading...' : _isEmpty(inputValue) ? 'No Studies' : `No Studies match ${inputValue}`
      }
    />
  );
};
